import { useParams } from "react-router-dom";
import type { ManualWorkloadKind } from "@k8s-dashboard/contracts";
import { useScope } from "@/api/queries";
import { Breadcrumb, currentRouteCrumb, routeCrumbAt } from "@/components/drill";
import { Panel } from "@/components/primitives";
import { PageHeader } from "@/features/drill/common";
import { useDashboardParams } from "@/state/useDashboardParams";
import { ManualWorkloadActions } from "./ManualWorkloadActions";
import { useT } from "@/i18n/I18nProvider";

const SUPPORTED = new Set<ManualWorkloadKind>(["Deployment", "StatefulSet", "DaemonSet"]);

/** Exact-target entry point. It intentionally performs no list/detail query. */
export function ManualWorkloadManageView() {
  const { namespace = "", kind = "", name = "" } = useParams();
  const { clusterId } = useDashboardParams();
  const scope = useScope();
  const { t } = useT("workload");
  const supported = SUPPORTED.has(kind as ManualWorkloadKind);
  const canManage = scope.data?.canManageWorkloads === true;

  return (
    <div className="page">
      <PageHeader
        title={name}
        subtitle={`${kind} · ${namespace} · ${clusterId}`}
        crumbs={<Breadcrumb items={[routeCrumbAt("overview", "/", ""), currentRouteCrumb("manage-workload")]} />}
      />
      <Panel title={t("workload.manage.title")} subtitle={`${namespace}/${name}`}>
        {!supported ? (
          <div className="state" role="alert"><span className="state__title">{t("workload.manage.unsupportedKind")}</span></div>
        ) : !canManage ? (
          <div className="state" role="alert"><span className="state__title">{t("workload.manage.permissionRequired")}</span></div>
        ) : (
          <ManualWorkloadActions
            clusterId={clusterId}
            kind={kind as ManualWorkloadKind}
            namespace={namespace}
            name={name}
            enabled={canManage}
          />
        )}
      </Panel>
    </div>
  );
}
